import React, { useState } from 'react';
import { Lightbulb, Loader2, Check, X, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const STATUS = { pending: ['Awaiting review', '#9a6b00'], accepted: ['Added to agenda', '#1c6b2c'], declined: ['Not added', '#8a1f11'] };

// Invited attendees propose agenda items; the chair accepts (adds to agenda) or declines.
export const AgendaSuggestions = ({ meeting, onUpdated }) => {
  const { suggestAgendaItem, decideAgendaSuggestion, user } = useAuth();
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [deciding, setDeciding] = useState(null);
  const rows = meeting.agenda_suggestions || [];
  const myEmail = (user?.email || '').toLowerCase();

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try { onUpdated(await suggestAgendaItem(meeting.id, { title: title.trim(), notes: notes.trim() })); setTitle(''); setNotes(''); toast.success('Suggestion sent to the chair.'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not send your suggestion.'); }
    setBusy(false);
  };
  const decide = async (sid, action) => {
    const note = action === 'decline' ? (window.prompt('Optional note to the member:', '') ?? null) : '';
    if (note === null) return;
    setDeciding(sid);
    try { onUpdated(await decideAgendaSuggestion(meeting.id, sid, { action, note })); toast.success(action === 'accept' ? 'Added to the agenda.' : 'Suggestion declined.'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not update the suggestion.'); }
    setDeciding(null);
  };

  return (
    <section className="mt-6" data-testid="agenda-suggestions">
      <h2 className="font-title text-xl mb-3 flex items-center gap-2" style={{ color: 'var(--maroon)' }}><Lightbulb size={18} /> Agenda suggestions</h2>
      {rows.length > 0 && (
        <ul className="space-y-2 mb-4">
          {rows.map((s) => {
            const [label, color] = STATUS[s.status] || STATUS.pending;
            return (
              <li key={s.id} className="bg-white rounded-lg border p-3" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid={`agenda-suggestion-${s.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-title" style={{ color: 'var(--navy)' }}>{s.title}</div>
                    <div className="text-xs mt-0.5" style={{ color: 'var(--olive)' }}>Suggested by {s.email?.toLowerCase() === myEmail ? 'you' : s.name}</div>
                    {s.notes && <p className="text-sm mt-1" style={{ color: 'var(--ink)' }}>{s.notes}</p>}
                    {s.decision_note && <p className="text-xs mt-1 italic" style={{ color: 'var(--olive)' }}>Chair: {s.decision_note}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-full text-xs font-title" style={{ border: `1px solid ${color}`, color }} data-testid={`agenda-suggestion-status-${s.id}`}>{label}</span>
                    {meeting.can_manage && s.status === 'pending' && (
                      <>
                        <button onClick={() => decide(s.id, 'accept')} disabled={deciding === s.id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded font-title" style={{ background: '#1c6b2c', color: 'white' }} data-testid={`agenda-suggestion-accept-${s.id}`}>{deciding === s.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Add to agenda</button>
                        <button onClick={() => decide(s.id, 'decline')} disabled={deciding === s.id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded font-title" style={{ border: '1px solid #8a1f11', color: '#8a1f11' }} data-testid={`agenda-suggestion-decline-${s.id}`}><X size={12} /> Decline</button>
                      </>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <form onSubmit={submit} className="bg-white rounded-lg border p-3 space-y-2" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="agenda-suggest-form">
        <p className="text-xs" style={{ color: 'var(--olive)' }}>Have something that should be discussed? Suggest it and the chair will review it before the meeting.</p>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Agenda item title" maxLength={200} className="w-full border rounded px-3 py-2 text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="agenda-suggest-title" />
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Why it matters / background (optional)" rows={2} className="w-full border rounded px-3 py-2 text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="agenda-suggest-notes" />
        <button type="submit" disabled={busy || !title.trim()} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded font-title disabled:opacity-50" style={{ background: 'var(--navy)', color: 'var(--cream)' }} data-testid="agenda-suggest-submit">{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Suggest item</button>
      </form>
    </section>
  );
};
